import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useRef, useState } from 'react';
import { useStores } from '../../stores';
import { observer } from 'mobx-react';
import {
  buildFusionChartDataSource,
  downloadCostTrendCsv,
  formatTrendParameters,
  getCostTrend,
  getFusionChartType,
  TrendAnalysisLocationProps,
} from '../../core-utils/helper-utils/trendAnalysisHelper';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import Reveal from 'reveal.js';
import Loader from '../../_components/molecules/Loader';
import ReportsTemplate from '../../_components/templates/ReportTemplate';
import {
  Button,
  Edit,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Typography from '@HomaHealth/ui-component-library/lib/_patient-access-components/atoms/Typography';
import html2canvas from 'html2canvas';
import PptxGenJS from 'pptxgenjs';
import jsPDF from 'jspdf';
import TrendAnalysisConstants from '../../core-utils/messages/TrendAnalysisConstants';
import { useAPIPayload } from '../../core-utils/helper-utils/apiPayloadHelper';
import { useNavigate, useLocation } from 'react-router-dom';
import { FusionCharts } from '@HomaHealth/ui-component-library';
import usePlanDates from '../../core-utils/custom-hooks/PlanDates';
import { commonDropDownProps } from '../../_components/atoms/ReportsCohortItem';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import axios from '../../core-utils/axios/axiosSetUp';
import properties from '../../core-utils/properties';
import { TREND_ANALYSIS } from '../../core-utils/routes';

const TrendAnalysisInternalPage = observer(() => {
  //Constants
  const { classes } = makeStyles()({
    root: {
      display: 'flex',
      flexWrap: 'nowrap',
      width: '100%',
      height: '100%',
    },
    reveal: {
      border: `10px solid ${patientAccessTheme.palette.grey[300]}`,
      height: '100%',
    },
    exportGrid: {
      margin: '20px',
      display: 'flex',
      gap: '15px',
      flexDirection: 'column',
    },
    switch: {
      display: 'flex',
      gap: '5px',
      alignItems: 'center',
    },
    exportNote: {
      position: 'absolute',
      backgroundColor: 'rgba(255, 255, 255, 0.6)',
      width: '70%',
      height: '70%',
      zIndex: 1,
      top: '20%',
      left: '10%',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    exportText: {
      padding: '20px',
      fontSize: '16px',
      fontFamily: 'Inter',
      fontWeight: 500,
      lineHeight: '16px',
      color: patientAccessTheme.palette.status.white,
      backgroundColor: patientAccessTheme.palette.grey[900],
      height: 'fit-content',
      width: 'fit-content',
    },
    flex: {
      display: 'flex',
      gap: '20px',
    },
  })();

  const quaterlyReportStore = useStores().quaterlyReportStore;
  const { isPlanDatesFetched } = quaterlyReportStore;
  const employerGroupStore = useStores().employerGroupStore;
  const { getPlanDates, loading: isPlanDatesLoading } = usePlanDates();
  const navigate = useNavigate();

  const deckDivRef = useRef<HTMLDivElement>(null);
  const deckRef = useRef<Reveal.Api | null>(null);
  const exportInProgressRef = useRef(false);
  const payload = useAPIPayload();
  const location: TrendAnalysisLocationProps = useLocation();
  const abortController = new AbortController();

  //States

  const [loading, setLoading] = useState<boolean>(false);
  const trend_id = location.state.trend_id;

  const [chartType, setChartType] = useState<string>('');
  const [chartDatasource, setChartDatasource] = useState<any>({});
  const [actualData, setActualData] = useState<any>([]);
  const [trendData, setTrendData] = useState<any>([]);

  //Functions
  const exportSlides = async (format: string) => {
    const trend = trendData?.trendList?.[0];
    const timeGrain =
      trend?.timeGrain || TrendAnalysisConstants.DEFAULT_TIME_GRAIN;
    const primaryAxisData = trend?.primaryAxisData || [];
    const secondaryAxisData = trend?.secondaryAxisData || [];

    if (format === 'csv') {
      downloadCostTrendCsv(
        actualData,
        primaryAxisData,
        secondaryAxisData,
        timeGrain,
      );
      return;
    }
    if (!deckDivRef.current || exportInProgressRef.current) {
       
      console.error(
        'deckDivRef reference is null or export already in progress',
      );
      return;
    }

    exportInProgressRef.current = true;

    // Disable Reveal.js controls
    if (deckRef.current) {
      deckRef.current.configure({ controls: false, keyboard: false });
    }

    const slides = Array.from(
      deckDivRef.current.querySelectorAll('.slides section'),
    );
    const slideNumber: any = deckRef.current?.getIndices();
    const currentSlide: any = slides[slideNumber.h];
    currentSlide.style.opacity = '0';

    const exportCanvas = async (slide: any) => {
      const originalOpacity = slide.style.opacity;
      const originalDisplay = slide.style.display;
      if (window.innerWidth < 1600) {
        slide.style.transform = 'scale(0.75)';
        slide.style.transformOrigin = 'top left';
        slide.style.width = '115%';
        slide.style.height = '110%';
      }
      slide.style.opacity = '1';
      slide.style.display = 'block';
      const canvas = await html2canvas(slide, { scale: 2 });

      slide.style.opacity = originalOpacity;
      slide.style.display = originalDisplay;
      if (window.innerWidth < 1600) {
        slide.style.transform = '';
        slide.style.transformOrigin = '';
        slide.style.width = '';
        slide.style.height = '';
      }
      return canvas;
    };

    try {
      if (format === 'pptx') {
        const pptx = new PptxGenJS();
        for (const slide of slides) {
          if (!exportInProgressRef.current) {
            return;
          }
          const canvas = await exportCanvas(slide);
          const imgData = canvas.toDataURL('image/png');

          const slidePPT = pptx.addSlide();
          slidePPT.addImage({
            data: imgData,
            x: 0,
            y: 0,
            w: '100%',
            h: '100%',
          });
        }
        await pptx.writeFile({ fileName: 'presentation.pptx' });
      } else if (format === 'pdf') {
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: [1310, 740],
        });
        for (let i = 0; i < slides.length; i++) {
          if (!exportInProgressRef.current) {
            return;
          }
          const canvas = await exportCanvas(slides[i]);
          const imgData = canvas.toDataURL('image/png');
          if (i !== 0) {
            pdf.addPage();
          }
          const imgProps = pdf.getImageProperties(imgData);
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        }
        pdf.save('presentation.pdf');
      }
    } finally {
      exportInProgressRef.current = false;
      currentSlide.style.opacity = '1';
      // Re-enable Reveal.js controls
      if (deckRef.current) {
        deckRef.current.configure({ controls: true, keyboard: true });
      }
    }
  };

  //Api Calls
  const apiCalls = async () => {
    try {
      const trend = trendData?.trendList?.[0];
      const timeGrain =
        trend?.timeGrain || TrendAnalysisConstants.DEFAULT_TIME_GRAIN;
      const isSecondaryAxis = trend?.isSecondaryAxis || false;
      const primaryAxisData =
        trend?.primaryAxisData ||
        TrendAnalysisConstants.DEFAULT_PRIMARY_AXIS_DATA;
      const secondaryAxisData =
        trend?.secondaryAxisData ||
        TrendAnalysisConstants.DEFAULT_SECONDARY_AXIS_DATA;
      const trends = formatTrendParameters(primaryAxisData, secondaryAxisData);
      const apis = [
        getCostTrend(
          {
            ...payload,
            timeGrain,
            trends,
            memberList: [], // 👈 no cohort filter
          },
          abortController.signal,
        ),
      ];

      const result: any = await Promise.allSettled(apis);
      const data = result[0].value;

      const chartType = getFusionChartType(
        isSecondaryAxis,
        primaryAxisData,
        secondaryAxisData,
      );

      const dataSource = buildFusionChartDataSource(
        data,
        primaryAxisData,
        secondaryAxisData,
        timeGrain,
        chartType,
      );

      setActualData(data);
      setChartType(chartType);
      setChartDatasource(dataSource);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendById = async () => {
    try {
      const res = await axios.post(
        properties.aiqBaseUrl +
          properties.deepDiveBaseUrl +
          properties.getTrendById,
        {
          trend_id,
        },
      );
      const trendData = res?.data?.data || [];
      setTrendData(trendData);
    } catch (error) {
      console.error('Error fetching trend data:', error);
    }
  };

  //useEffects
  useEffect(() => {
    if (loading) {
      return;
    }

    if (deckRef.current) {
      deckRef.current.destroy();
      deckRef.current = null;
    }

    // Wait for React to render DOM updates after loading = false
    const timeout = setTimeout(() => {
      if (deckDivRef.current) {
        deckRef.current = new Reveal(deckDivRef.current, {
          controls: true,
          progress: true,
          slideNumber: true,
          history: false,
          center: false,
          transition: 'none',
          width: 1320,
          height: 740,
          embedded: true,
          viewDistance: 30,
          disableLayout: true,
        });

        deckRef.current.initialize();
      }
    }, 100); // small delay to let DOM update

    return () => {
      clearTimeout(timeout);
      try {
        if (deckRef.current) {
          deckRef.current.destroy();
          deckRef.current = null;
        }
      } catch (e) {
         
        console.error('Reveal.js destroy call failed.');
      }
    };
  }, [loading]);

  useEffect(() => {
    setLoading(true);
    getTrendById();
    return () => {
      abortController.abort();
    };
  }, [trend_id]);

  useEffect(() => {
    if (!trendData?.trendList?.length) {
      return;
    }
    apiCalls();
    return () => {
      abortController.abort();
    };
  }, [trendData]);

  useEffect(() => {
    const fetchPlanDates = async () => {
      if (!isPlanDatesFetched) {
        await getPlanDates();
      }
    };
    if (!employerGroupStore?.employerGroupResponseData?.eg_nid[0]) {
      return;
    }
    fetchPlanDates();
  }, [employerGroupStore?.employerGroupResponseData?.eg_nid]);

  //Components
  const Export = () => {
    const [exportFormat, setExportFormat] = useState<string>('pdf');
    const [isExport, setIsExport] = useState<boolean>(false);
    return (
      <div className={classes.exportGrid}>
        <div
          style={{ cursor: 'pointer' }}
          className={classes.switch}
          onClick={() => {
            navigate(TREND_ANALYSIS, {
              state: {
                triggerEdit: trendData,
              },
            });
          }}
        >
          <Edit />{' '}
          <Typography
            fontSize="1rem"
            fontType="medium"
            textColor={patientAccessTheme.palette.text.highemphasis}
          >
            {TrendAnalysisConstants.EDIT_ANALYSIS}
          </Typography>
        </div>
        <div className={classes.switch}>
          <Typography
            fontSize="12px"
            fontType="medium"
            textColor={patientAccessTheme.palette.text.highemphasis}
          >
            {TrendAnalysisConstants.EXPORT}
          </Typography>
          <Dropdown
            label={TrendAnalysisConstants.EXPORT}
            dropDownOptions={TrendAnalysisConstants.EXPORT_OPTIONS}
            dropDownValues={TrendAnalysisConstants.EXPORT_VALUES}
            value={exportFormat}
            setValue={(value: string) => setExportFormat(value)}
            selectStyles={{
              ...commonDropDownProps.selectStyles,
              width: '75px',
              height: '25px',
            }}
            labelTop="10px"
            onChange={(event: any) => {
              setExportFormat(event.target.value as string);
            }}
          />
        </div>
        <Button
          onClick={async () => {
            setIsExport(true);
            await exportSlides(exportFormat);
            setIsExport(false);
          }}
          height="25px"
          variant="contained"
          label={'Export'}
          width="60px"
          disabled={isExport}
        />
        {isExport && (
          <div className={classes.exportNote}>
            <div className={classes.exportText}>
              Please wait until the report is being downloaded
            </div>
          </div>
        )}
      </div>
    );
  };

  const PptSlides = () => {
    return (
      <Grid style={{ width: '89%' }}>
        <div className={`reveal ${classes.reveal}`} ref={deckDivRef}>
          <div className="slides">
            {loading || isPlanDatesLoading ? (
              <Loader />
            ) : (
              <>
                <ReportsTemplate
                  pageTitle={
                    <Grid style={{ margin: '2.5rem 1rem 1rem' }}>
                      <PageTitleAndSubTitle
                        title={
                          <Typography
                            fontSize="26px"
                            fontType="semiBold"
                            textColor={patientAccessTheme.palette.grey[700]}
                            align="left"
                          >
                            {`${TrendAnalysisConstants.TITLE} - ${
                              trendData?.trendName || ''
                            }`}
                          </Typography>
                        }
                        subTitle={''}
                      />
                    </Grid>
                  }
                  body={
                    <Grid style={{ marginTop: '15px' }}>
                      <FusionCharts
                        typeOfChart={chartType}
                        widthOfChart={1150}
                        heightOfChart={470}
                        dataSource={chartDatasource}
                      />
                    </Grid>
                  }
                />
              </>
            )}
          </div>
        </div>
      </Grid>
    );
  };

  return (
    <>
      <Grid container className={classes.root}>
        <PptSlides />
        <Export />
      </Grid>
    </>
  );
});

export default TrendAnalysisInternalPage;
